import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LoadingService } from './loading.service';

@Injectable({
  providedIn: 'root'
})
export class RouterLoadingService {
  private isNavigating = false;

  constructor(
    private router: Router,
    private loadingService: LoadingService
  ) {
    this.initializeRouterLoading();
  }

  private initializeRouterLoading(): void {
    // Listen to router events
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart || event instanceof NavigationEnd)
    ).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.handleNavigationStart(event);
      } else if (event instanceof NavigationEnd) {
        this.handleNavigationEnd(event);
      }
    });
  }

  private handleNavigationStart(event: NavigationStart): void {
    // Don't show loading for initial navigation or when navigating to the same route
    if (event.id === 1 || this.isNavigating) {
      return;
    }

    this.isNavigating = true;
    
    // Determine the page name from the URL for a more specific message
    const pageName = this.getPageNameFromUrl(event.url);
    const message = pageName ? `Loading ${pageName}...` : 'Loading page...';
    
    this.loadingService.showPageLoading(message);
  }

  private handleNavigationEnd(event: NavigationEnd): void {
    if (this.isNavigating) {
      this.isNavigating = false;
      
      // Add a small delay to ensure the page has time to render
      setTimeout(() => {
        this.loadingService.hidePageLoading();
      }, 100);
    }
  }

  private getPageNameFromUrl(url: string): string {
    // Remove query parameters and fragments
    const cleanUrl = url.split('?')[0].split('#')[0];
    
    // Extract the main route segment
    const segments = cleanUrl.split('/').filter(segment => segment);
    
    if (segments.length === 0) {
      return 'Dashboard';
    }

    // Map common routes to friendly names
    const routeNameMap: { [key: string]: string } = {
      'dashboard': 'Dashboard',
      'profile': 'Profile',
      'products': 'Products',
      'product-management': 'Product Management',
      'categories': 'Categories',
      'user-management': 'User Management',
      'user-tree-view': 'User Tree',
      'hierarchy': 'Hierarchy',

      'status-management': 'Status Management',
      'feature-master': 'Feature Master',
      'admin': 'Admin Panel',
      'auth': 'Authentication',
      'login': 'Login',
      'register': 'Registration',
      'forgot-password': 'Password Recovery',
      'reset-password': 'Password Reset'
    };

    const mainSegment = segments[0];
    return routeNameMap[mainSegment] || this.capitalizeWords(mainSegment);
  }

  private capitalizeWords(str: string): string {
    return str
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Manually trigger page loading (useful for programmatic navigation)
   */
  public showPageLoading(message?: string): void {
    this.loadingService.showPageLoading(message);
  }

  /**
   * Manually hide page loading
   */
  public hidePageLoading(): void {
    this.loadingService.hidePageLoading();
  }
}